import { useEffect, useMemo, useState } from 'react';
import { AppShell } from '@/components/layout/AppShell';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { useAuth } from '@/lib/auth';
import { useFeature } from '@/lib/features';
import {
  FileText, Mail, Plus, CheckCircle2, XCircle, Clock, Inbox, User, Building2,
  ExternalLink, ShieldCheck, Layers,
} from 'lucide-react';

const KIND_ICON = { email: Mail, file: FileText };
const KIND_LABEL = { email: 'Email template', file: 'File / Deck template' };
const STATUS_CHIP = {
  pending: 'chip-warning',
  approved: 'chip-success',
  rejected: 'chip-danger',
};

const APPROVER_ROLES = new Set(['owner', 'operations', 'dm_creative', 'dm_technical', 'dm_architecture', 'accounts', 'commercials', 'hr']);

export default function TemplatesPage() {
  const { user } = useAuth();
  const enabled = useFeature('template_contributions');
  const [params, setParams] = useSearchParams();
  const initialTab = params.get('tab') || 'library';
  const [tab, setTab] = useState(initialTab);

  const [meta, setMeta] = useState(null);
  const [decks, setDecks] = useState([]);
  const [emails, setEmails] = useState([]);
  const [contributions, setContributions] = useState([]);
  const [reviewQueue, setReviewQueue] = useState([]);
  const [mine, setMine] = useState([]);
  const [openContribute, setOpenContribute] = useState(false);
  const [openDetail, setOpenDetail] = useState(null);
  const [openDecision, setOpenDecision] = useState(null); // {contribution, decision: 'approve'|'reject'}
  const [loading, setLoading] = useState(true);

  const isApprover = user && APPROVER_ROLES.has(user.role);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [m, d, e, all, mineL] = await Promise.all([
        api.get('/template_contributions/meta'),
        api.get('/templates/decks'),
        api.get('/templates/emails'),
        api.get('/template_contributions?scope=all'),
        api.get('/template_contributions?scope=mine'),
      ]);
      setMeta(m.data);
      setDecks(d.data || []);
      setEmails(e.data || []);
      setContributions(all.data || []);
      setMine(mineL.data || []);
      if (isApprover) {
        const rev = await api.get('/template_contributions?scope=to_review');
        setReviewQueue(rev.data || []);
      }
    } catch (err) {
      toast.error(parseErr(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    setParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    }, { replace: true });
  }, [tab]);

  return (
    <AppShell breadcrumbs={[{ label: 'Templates' }]}>
      <div className="p-6 max-w-[1400px] mx-auto space-y-4 fade-up" data-testid="templates-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading text-2xl font-semibold flex items-center gap-2">
              <FileText size={22} />Templates
            </h1>
            <p className="text-[13px] text-[hsl(var(--foreground-muted))] mt-1 max-w-[680px]">
              Deck &amp; email templates the studio reaches for at each stage. Anyone can suggest a new one — it routes to the
              right lead for approval. Files live in Drive; this hub keeps canonical references.
            </p>
          </div>
          {enabled && (
            <Button onClick={() => setOpenContribute(true)} data-testid="templates-contribute-cta">
              <Plus size={14} className="mr-1" />Suggest a template
            </Button>
          )}
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="templates-tabs">
            <TabsTrigger value="library" data-testid="templates-tab-library">
              <FileText size={13} className="mr-1" />Library
            </TabsTrigger>
            <TabsTrigger value="mine" data-testid="templates-tab-mine">
              <User size={13} className="mr-1" />My suggestions {mine.length ? `(${mine.length})` : ''}
            </TabsTrigger>
            {isApprover && (
              <TabsTrigger value="review" data-testid="templates-tab-review">
                <Inbox size={13} className="mr-1" />To review {reviewQueue.length ? `(${reviewQueue.length})` : ''}
              </TabsTrigger>
            )}
          </TabsList>
        </Tabs>

        {loading && <div className="text-[13px] text-[hsl(var(--foreground-muted))] py-8 text-center">Loading…</div>}

        {!loading && tab === 'library' && (
          <LibraryView decks={decks} emails={emails} approvedContributions={contributions.filter(c => c.status === 'approved')} onOpen={setOpenDetail} />
        )}

        {!loading && tab === 'mine' && (
          <ContributionList
            rows={mine}
            empty="You have not suggested any templates yet. Click Suggest a template to add one."
            emptyCta={enabled ? () => setOpenContribute(true) : null}
            onOpen={setOpenDetail}
            currentUserId={user.id}
          />
        )}

        {!loading && tab === 'review' && isApprover && (
          <ContributionList
            rows={reviewQueue}
            empty="Nothing waiting for your review. When someone suggests a template routed to you, it'll appear here."
            onOpen={setOpenDetail}
            onDecide={(c, d) => { setOpenDetail(null); setOpenDecision({ contribution: c, decision: d }); }}
            currentUserId={user.id}
            showApproverActions
          />
        )}
      </div>

      {openContribute && meta && (
        <ContributeDialog
          meta={meta}
          onClose={() => setOpenContribute(false)}
          onCreated={() => { setOpenContribute(false); loadAll(); setTab('mine'); }}
        />
      )}

      {openDetail && (
        <DetailDialog
          contribution={openDetail}
          onClose={() => setOpenDetail(null)}
          isApprover={isApprover}
          currentUserId={user.id}
          onDecide={(d) => setOpenDecision({ contribution: openDetail, decision: d })}
        />
      )}

      {openDecision && (
        <DecisionDialog
          contribution={openDecision.contribution}
          decision={openDecision.decision}
          onClose={() => setOpenDecision(null)}
          onDone={() => { setOpenDecision(null); loadAll(); }}
        />
      )}
    </AppShell>
  );
}

// ============================================================ Library view

function LibraryView({ decks, emails, approvedContributions, onOpen }) {
  const approvedEmails = approvedContributions.filter(c => c.kind === 'email');
  const approvedFiles = approvedContributions.filter(c => c.kind === 'file');
  return (
    <div className="space-y-6">
      <LibrarySection
        title="Email templates"
        icon={Mail}
        seedItems={emails}
        contributedItems={approvedEmails}
        onOpenContribution={onOpen}
      />
      <LibrarySection
        title="Deck & file templates"
        icon={FileText}
        seedItems={decks}
        contributedItems={approvedFiles}
        onOpenContribution={onOpen}
      />
    </div>
  );
}

function LibrarySection({ title, icon: Icon, seedItems, contributedItems, onOpenContribution }) {
  return (
    <section className="space-y-2" data-testid={`templates-section-${title.replace(/\s/g, '-').toLowerCase()}`}>
      <div className="flex items-center gap-2">
        <Icon size={16} className="text-[hsl(var(--foreground-muted))]" />
        <h2 className="font-heading font-semibold">{title}</h2>
        <span className="text-[11.5px] text-[hsl(var(--foreground-subtle))]">
          · {seedItems.length + contributedItems.length} available
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {seedItems.map(t => (
          <Link
            key={t.id}
            to={`/knowledge/${t.id}`}
            className="card p-4 hover:border-[hsl(var(--border-strong))] transition-colors block"
            data-testid={`templates-canonical-${t.id}`}
          >
            <div className="flex items-center gap-2 text-[hsl(var(--foreground-muted))]">
              <span className="text-[11px] uppercase tracking-wider">Canonical</span>
            </div>
            <div className="font-heading font-semibold mt-1">{t.title}</div>
            <div className="text-[12.5px] text-[hsl(var(--foreground-muted))] mt-1 line-clamp-2">
              {t.blocks?.find(b => b.type === 'paragraph')?.text}
            </div>
          </Link>
        ))}
        {contributedItems.map(c => (
          <button
            key={c.id}
            onClick={() => onOpenContribution(c)}
            className="card p-4 text-left hover:border-[hsl(var(--border-strong))] transition-colors relative"
            data-testid={`templates-contributed-${c.id}`}
          >
            <div className="flex items-center gap-2 text-[hsl(var(--foreground-muted))]">
              <ShieldCheck size={12} className="text-[hsl(var(--success))]" />
              <span className="text-[11px] uppercase tracking-wider">Contributed</span>
              <ScopeChip department={c.department} stage={c.stage} />
            </div>
            <div className="font-heading font-semibold mt-1">{c.title}</div>
            <div className="text-[12.5px] text-[hsl(var(--foreground-muted))] mt-1 line-clamp-2">{c.description}</div>
            <div className="text-[11.5px] text-[hsl(var(--foreground-subtle))] mt-2">
              By {c.proposer_name || c.proposer_email}
            </div>
          </button>
        ))}
      </div>
      {seedItems.length + contributedItems.length === 0 && (
        <div className="text-[13px] text-[hsl(var(--foreground-muted))] py-6 text-center border rounded-md" style={{ borderColor: 'hsl(var(--border))' }}>
          Nothing here yet.
        </div>
      )}
    </section>
  );
}

// ============================================================ Contribution list

function ContributionList({ rows, empty, emptyCta, onOpen, onDecide, currentUserId, showApproverActions }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="text-center py-10 border rounded-lg" style={{ borderColor: 'hsl(var(--border))' }} data-testid="templates-empty">
        <div className="text-[hsl(var(--foreground-muted))] text-[13.5px] max-w-[420px] mx-auto">{empty}</div>
        {emptyCta && (
          <Button className="mt-3" onClick={emptyCta} data-testid="templates-empty-cta">
            <Plus size={13} className="mr-1" />Suggest a template
          </Button>
        )}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {rows.map(c => {
        const Icon = KIND_ICON[c.kind] || FileText;
        return (
          <div
            key={c.id}
            className="card p-4 flex flex-col gap-2"
            data-testid={`templates-row-${c.id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 text-[hsl(var(--foreground-muted))] text-[11px] uppercase tracking-wider">
                  <Icon size={12} />{KIND_LABEL[c.kind]}
                </div>
                <button
                  className="font-heading font-semibold mt-0.5 text-left hover:underline"
                  onClick={() => onOpen(c)}
                >
                  {c.title}
                </button>
                <div className="text-[12.5px] text-[hsl(var(--foreground-muted))] line-clamp-2 mt-1">{c.description}</div>
              </div>
              <span className={`chip ${STATUS_CHIP[c.status]}`} data-testid={`templates-status-${c.id}`}>{c.status}</span>
            </div>
            <div className="flex flex-wrap gap-1 items-center text-[11.5px]">
              <ScopeChip department={c.department} stage={c.stage} />
              <span className="text-[hsl(var(--foreground-subtle))]">·</span>
              <span className="text-[hsl(var(--foreground-subtle))]">
                By {c.proposer_name || c.proposer_email}
              </span>
            </div>
            {c.status === 'rejected' && c.decision_note && (
              <div className="text-[12px] text-[hsl(var(--danger))] italic border-l-2 pl-2 mt-1" style={{ borderColor: 'hsl(var(--danger))' }}>
                <b>Rejected:</b> {c.decision_note}
              </div>
            )}
            {c.status === 'approved' && c.decision_note && (
              <div className="text-[12px] text-[hsl(var(--foreground-muted))] italic border-l-2 pl-2 mt-1" style={{ borderColor: 'hsl(var(--success))' }}>
                <b>Approved:</b> {c.decision_note}
              </div>
            )}
            {showApproverActions && c.status === 'pending' && c.proposer_user_id !== currentUserId && (
              <div className="flex gap-2 justify-end mt-1">
                <Button size="sm" variant="outline" onClick={() => onDecide(c, 'reject')} data-testid={`templates-reject-${c.id}`}>
                  <XCircle size={13} className="mr-1" />Reject
                </Button>
                <Button size="sm" onClick={() => onDecide(c, 'approve')} data-testid={`templates-approve-${c.id}`}>
                  <CheckCircle2 size={13} className="mr-1" />Approve
                </Button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ScopeChip({ department, stage }) {
  return (
    <span className="chip inline-flex items-center gap-1 whitespace-nowrap">
      <Building2 size={10} />{department}
      <span className="opacity-40">·</span>
      <Layers size={10} />{stage.replace(/_/g, ' ')}
    </span>
  );
}

// ============================================================ Contribute dialog

function ContributeDialog({ meta, onClose, onCreated }) {
  const [kind, setKind] = useState('email');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [department, setDepartment] = useState('general');
  const [stage, setStage] = useState('any');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [driveLink, setDriveLink] = useState('');
  const [fileDescription, setFileDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const routingHint = useMemo(() => {
    if (!meta) return '';
    return kind === 'email' ? meta.routing_rules.email : meta.routing_rules.file;
  }, [kind, meta]);

  const canSave = title.trim().length > 0 && description.trim().length > 0 && (
    kind === 'email' ? emailSubject.trim() && emailBody.trim() : driveLink.trim()
  );

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        title: title.trim(),
        description: description.trim(),
        kind,
        department,
        stage,
        payload: {
          email_subject: kind === 'email' ? emailSubject.trim() : null,
          email_body: kind === 'email' ? emailBody.trim() : null,
          drive_link: kind === 'file' ? driveLink.trim() : null,
          file_description: kind === 'file' ? (fileDescription.trim() || null) : null,
        },
      };
      await api.post('/template_contributions', body);
      toast.success('Template suggestion sent for review.');
      onCreated();
    } catch (e) {
      toast.error(parseErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[720px]" data-testid="templates-contribute-dialog">
        <DialogHeader>
          <DialogTitle>Suggest a template</DialogTitle>
          <DialogDescription>
            Templates go through a light approval so we do not accumulate near-duplicates. Approvers see this exactly as written.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Kind</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <KindChoice active={kind === 'email'} onClick={() => setKind('email')} label="Email" hint="Subject + body copy with placeholders." icon={Mail} testId="templates-kind-email" />
              <KindChoice active={kind === 'file'} onClick={() => setKind('file')} label="File / Deck" hint="Drive link + description." icon={FileText} testId="templates-kind-file" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Title</Label>
              <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Concept sign-off confirmation email" data-testid="templates-title" />
            </div>
            <div>
              <Label>Department</Label>
              <select className="h-9 w-full border rounded-md px-2 text-[14px] mt-1"
                      style={{ borderColor: 'hsl(var(--border))' }}
                      value={department} onChange={e => setDepartment(e.target.value)} data-testid="templates-department">
                {meta.departments.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
              </select>
            </div>
            <div>
              <Label>Stage</Label>
              <select className="h-9 w-full border rounded-md px-2 text-[14px] mt-1"
                      style={{ borderColor: 'hsl(var(--border))' }}
                      value={stage} onChange={e => setStage(e.target.value)} data-testid="templates-stage">
                {meta.stages.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
            <div className="md:col-span-1 self-end">
              <div className="text-[11.5px] text-[hsl(var(--foreground-muted))] leading-snug">
                <ShieldCheck size={11} className="inline mr-1 text-[hsl(var(--success))]" />
                {routingHint}
              </div>
            </div>
          </div>

          <div>
            <Label>Why this template? When to use it?</Label>
            <Textarea rows={2} value={description} onChange={e => setDescription(e.target.value)}
                      placeholder="A short use-case paragraph so the approver understands the intent." data-testid="templates-description" />
          </div>

          {kind === 'email' ? (
            <>
              <div>
                <Label>Email subject</Label>
                <Input value={emailSubject} onChange={e => setEmailSubject(e.target.value)}
                       placeholder="Use {placeholder_name} for dynamic fields — e.g. {project_name}"
                       data-testid="templates-email-subject" />
              </div>
              <div>
                <Label>Email body</Label>
                <Textarea rows={7} value={emailBody} onChange={e => setEmailBody(e.target.value)}
                          placeholder={'Hi {contact_first_name},\n\n...\n\nWarm regards,\n{sender_name}'}
                          data-testid="templates-email-body" />
                <div className="text-[11px] text-[hsl(var(--foreground-subtle))] mt-1">
                  Use <code>&#123;placeholder&#125;</code> style tokens. Amount-related placeholders are auto-masked at render time for teammates without amount visibility.
                </div>
              </div>
            </>
          ) : (
            <>
              <div>
                <Label>Drive link</Label>
                <Input value={driveLink} onChange={e => setDriveLink(e.target.value)}
                       placeholder="https://drive.google.com/…" data-testid="templates-drive-link" />
              </div>
              <div>
                <Label>File description (optional)</Label>
                <Textarea rows={3} value={fileDescription} onChange={e => setFileDescription(e.target.value)}
                          placeholder="What's inside, how to use it, when to print, etc." data-testid="templates-file-description" />
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={!canSave || saving} data-testid="templates-contribute-save">
            {saving ? 'Sending…' : 'Send for approval'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Label = ({ children }) => (
  <div className="text-[12px] text-[hsl(var(--foreground-muted))] mb-1">{children}</div>
);

function KindChoice({ active, onClick, label, hint, icon: Icon, testId }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-xl border p-3 text-left transition-colors ${active ? 'border-[hsl(var(--primary))] bg-[hsl(var(--accent))]' : 'hover:border-[hsl(var(--border-strong))]'}`}
      style={{ borderColor: active ? 'hsl(var(--primary))' : 'hsl(var(--border))' }}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 font-heading font-semibold text-[14px]"><Icon size={14} />{label}</div>
      <div className="text-[12px] text-[hsl(var(--foreground-muted))] mt-1">{hint}</div>
    </button>
  );
}

// ============================================================ Detail dialog

function DetailDialog({ contribution, onClose, isApprover, currentUserId, onDecide }) {
  const c = contribution;
  const Icon = KIND_ICON[c.kind] || FileText;
  const canDecide = isApprover && c.status === 'pending' && c.proposer_user_id !== currentUserId
    && (c.approver_user_ids || []).includes(currentUserId);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[720px]" data-testid="templates-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon size={16} />{c.title}
            <span className={`chip ${STATUS_CHIP[c.status]}`}>{c.status}</span>
          </DialogTitle>
          <DialogDescription>
            <ScopeChip department={c.department} stage={c.stage} />
            <span className="text-[11.5px] text-[hsl(var(--foreground-subtle))] ml-2">
              · By {c.proposer_name || c.proposer_email}
            </span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
          <div>
            <Label>Use case</Label>
            <div className="text-[13.5px]">{c.description}</div>
          </div>
          {c.kind === 'email' && (
            <>
              <div>
                <Label>Subject</Label>
                <div className="font-mono text-[12.5px] bg-[hsl(var(--surface-2))] rounded p-2">
                  {c.payload.email_subject || <em>—</em>}
                </div>
              </div>
              <div>
                <Label>Body</Label>
                <pre className="font-mono text-[12.5px] whitespace-pre-wrap bg-[hsl(var(--surface-2))] rounded p-3">
{c.payload.email_body || '—'}
                </pre>
              </div>
            </>
          )}
          {c.kind === 'file' && (
            <>
              <div>
                <Label>Drive link</Label>
                {c.payload.drive_link ? (
                  <a href={c.payload.drive_link} target="_blank" rel="noreferrer"
                     className="text-[13px] text-[hsl(var(--primary))] inline-flex items-center gap-1 hover:underline"
                     data-testid="templates-detail-drive">
                    Open in Drive <ExternalLink size={11} />
                  </a>
                ) : <em>—</em>}
              </div>
              {c.payload.file_description && (
                <div>
                  <Label>File description</Label>
                  <div className="text-[13.5px]">{c.payload.file_description}</div>
                </div>
              )}
            </>
          )}
          {c.decision_note && (
            <div>
              <Label>{c.status === 'approved' ? 'Approval note' : 'Rejection reason'}</Label>
              <div className="text-[13px] italic">
                &ldquo;{c.decision_note}&rdquo; — <span className="text-[hsl(var(--foreground-muted))]">{c.decided_by_email}</span>
              </div>
            </div>
          )}
          <div>
            <Label>Approvers</Label>
            <div className="flex flex-wrap gap-1">
              {(c.approver_summary || []).map(a => (
                <span key={a.user_id} className="chip">
                  {a.name || a.email}
                  <span className="text-[hsl(var(--foreground-subtle))] ml-1">· {a.role.replace('_', ' ')}</span>
                </span>
              ))}
              {(!c.approver_summary || c.approver_summary.length === 0) && <em className="text-[hsl(var(--foreground-muted))] text-[12px]">—</em>}
            </div>
          </div>
          <div>
            <Label>Activity</Label>
            <ul className="space-y-1 text-[12px]" data-testid="templates-detail-activity">
              {(c.activity || []).map((a, i) => (
                <li key={i} className="flex gap-2">
                  <Clock size={11} className="mt-0.5 text-[hsl(var(--foreground-subtle))] flex-none" />
                  <div>
                    <b className="capitalize">{a.action}</b>
                    {' — '}
                    <span className="text-[hsl(var(--foreground-muted))]">{a.actor_email}</span>
                    {a.note && <span className="italic"> · &ldquo;{a.note}&rdquo;</span>}
                    <span className="text-[hsl(var(--foreground-subtle))] ml-1">
                      · {new Date(a.at).toLocaleString()}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {canDecide && (
            <>
              <Button variant="outline" onClick={() => onDecide('reject')} data-testid="templates-detail-reject">
                <XCircle size={13} className="mr-1" />Reject
              </Button>
              <Button onClick={() => onDecide('approve')} data-testid="templates-detail-approve">
                <CheckCircle2 size={13} className="mr-1" />Approve
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================ Decision dialog

function DecisionDialog({ contribution, decision, onClose, onDone }) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const isApprove = decision === 'approve';
  const canSave = note.trim().length >= 3;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const url = `/template_contributions/${contribution.id}/${decision}`;
      await api.post(url, { note: note.trim() });
      toast.success(isApprove ? 'Approved and published to the library.' : 'Rejected with a written reason.');
      onDone();
    } catch (e) {
      toast.error(parseErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="templates-decision-dialog">
        <DialogHeader>
          <DialogTitle>{isApprove ? 'Approve template' : 'Reject template'} — &ldquo;{contribution.title}&rdquo;</DialogTitle>
          <DialogDescription>
            {isApprove
              ? 'Add a short note so the proposer sees your reasoning. Both approvers and the proposer are notified.'
              : 'A rejection must include one line of feedback. The proposer receives the note and can iterate.'}
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label>{isApprove ? 'Approval note' : 'Rejection reason'} (required)</Label>
          <Textarea
            rows={4}
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder={isApprove ? 'e.g. Clean and lands the intent, ship it.' : 'e.g. Overlaps with existing X — please merge into that template instead.'}
            data-testid="templates-decision-note"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={save}
            disabled={!canSave || saving}
            data-testid="templates-decision-save"
            variant={isApprove ? 'default' : 'destructive'}
          >
            {saving ? 'Saving…' : isApprove ? 'Approve' : 'Reject'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
